import {useState, type FormEvent} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {PageHeading} from '@/components/page-heading'
import {PerfilBloque} from '@/components/perfil-bloque'
import {PerfilBloquePeligros} from '@/components/perfil-bloque-peligros'
import {PerfilBloqueTabla} from '@/components/perfil-bloque-tabla'
import {PerfilLineaDialog} from '@/components/perfil-linea-dialog'
import {PerfilRiesgoDialog} from '@/components/perfil-riesgo-dialog'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {validateRequired} from '@/lib/validation'
import {
  borradorConRiesgo,
  borradorSinRiesgo,
  CON_DESCRIPCION,
  CONFIRMADAS_AL_ENTRAR,
  medidasQueSeVan,
  type Apertura,
  type AperturaRiesgo,
  type Bloque,
  type Riesgo,
  type RiesgoEscrito,
} from '@/lib/perfil-bloques'
import {AlcanceCambioDialog} from '@/components/alcance-cambio-dialog'
import {PerfilAlcanceDialog} from '@/components/perfil-alcance-dialog'
import {PerfilCargoTabs} from '@/components/perfil-cargo-tabs'
import {TrazabilidadSwitch, useTrazabilidad} from '@/components/perfil/provenance-note'
import {PersonPicker} from '@/components/investigacion/person-picker'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useAlcancePerfil,
  useConfirmarPerfil,
  useGuardarBorrador,
  usePerfilCargo,
  type PerfilAlcance,
  type PerfilCargoDetalle,
  type PerfilLineaRead,
  type PerfilLineaWrite,
} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {downloadBlob} from '@/lib/api'
import {motivoDelError, translateApiError} from '@/lib/errors'
import {formatDateOnly, formatInstantDate} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

type Cabecera = {
  jornada: string
  sede: string
  area: string
  jefe: string | null
}

/** El origen no viaja: el servidor lo vuelve a resolver desde la clave. */
function lineasGuardadas(detalle: PerfilCargoDetalle): PerfilLineaWrite[] {
  return detalle.bloques.flatMap((bloque) =>
    bloque.lineas.map((linea) => ({
      clave: linea.clave,
      bloque: linea.bloque,
      texto: linea.texto,
      // Las funciones guardadas antes de KAI-146 quedan confirmadas al próximo guardado.
      confirmado: linea.confirmado || CONFIRMADAS_AL_ENTRAR.has(linea.bloque as Bloque),
      // El PUT manda el borrador entero: sin su tipo o su peligro padre, la línea los perdería.
      subtipo: linea.subtipo,
      padre_clave: linea.bloque === 'medidas_prevencion' ? linea.padre_clave : null,
      clasificacion_gtc:
        linea.bloque === 'peligros' ? (linea.clasificacion_gtc as PerfilLineaWrite['clasificacion_gtc']) : null,
    }))
  )
}

type CambioDelDiff = {bloque: string; texto: string; cambio: string}

function diffDelError(err: unknown): CambioDelDiff[] | null {
  const cambios = motivoDelError(err)?.cambios
  if (!Array.isArray(cambios)) return null
  return cambios.filter(
    (cambio): cambio is CambioDelDiff =>
      typeof cambio === 'object' && cambio !== null && typeof (cambio as CambioDelDiff).texto === 'string'
  )
}

/** Nunca escribe sola: lo derivado solo entra cuando alguien lo agrega, y confirmar pasa por el alcance (E10-H02). */
export function PerfilCargoDetallePage() {
  const {t} = useTranslation()
  const {businessId, positionId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const detalle = usePerfilCargo(business ? businessId : undefined, positionId)
  const guardar = useGuardarBorrador(businessId!, positionId!)
  const pedirAlcance = useAlcancePerfil(businessId!, positionId!)
  const confirmar = useConfirmarPerfil(businessId!, positionId!)
  const [error, setError] = useState<string | null>(null)
  const [errorFirma, setErrorFirma] = useState<string | null>(null)
  const [diff, setDiff] = useState<CambioDelDiff[] | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [alcance, setAlcance] = useState<PerfilAlcance | null>(null)
  // Vacío hasta que llegue el alcance: el hoy del negocio lo da el servidor (`bogota_today`), no el navegador.
  const [exigible, setExigible] = useState('')
  const [motivo, setMotivo] = useState('')
  const [apertura, setApertura] = useState<Apertura | null>(null)
  const [riesgo, setRiesgo] = useState<AperturaRiesgo | null>(null)
  const [riesgoAQuitar, setRiesgoAQuitar] = useState<Riesgo | null>(null)
  const [cabecera, setCabecera] = useState<Cabecera | null>(null)
  // `downloadBlob` no atrapa nada: suelto en el `onClick`, su rechazo quedaba sin dueño.
  const [bajando, setBajando] = useState(false)
  const [errorDocumento, setErrorDocumento] = useState<string | null>(null)
  // Uno solo para toda la ficha; `useTrazabilidad` lo siembra desde `?desplegado=1`.
  const [trazabilidad, setTrazabilidad] = useTrazabilidad()

  if (guard) return guard
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage
        feature={t('plans.feature.perfilesCargo')}
        businessId={businessId}
        businessName={business!.name}
      />
    )
  }
  if (detalle.isError) {
    return (
      <div className="space-y-3">
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-detalle-error-carga">
          {t('perfilCargo.detalle.errorCarga')}
        </p>
        <Button size="sm" variant="outline" onClick={() => void detalle.refetch()}>
          {t('perfilCargo.reintentar')}
        </Button>
      </div>
    )
  }
  if (!detalle.data) return <Skeleton className="h-96 w-full" data-testid="perfil-detalle-cargando" />

  const perfil = detalle.data
  const editable = !godReadOnly && perfil.estado !== 'confirmado'
  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  const guardada: Cabecera = {
    jornada: perfil.cabecera.jornada_declarada ?? '',
    sede: perfil.cabecera.sede_cabecera ?? '',
    area: perfil.cabecera.area_cabecera ?? '',
    jefe: perfil.cabecera.jefe_employee_id,
  }
  const actual = cabecera ?? guardada
  const peligrosGuardados = perfil.bloques.find((bloque) => bloque.bloque === 'peligros')?.lineas ?? []
  const sucio = cabecera !== null

  async function bajarDocumento() {
    setErrorDocumento(null)
    setBajando(true)
    try {
      await downloadBlob(
        `/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}/documento`,
        `perfil-de-cargo-${perfil.cargo}.docx`
      )
    } catch (err) {
      setErrorDocumento(translateApiError(err))
    } finally {
      setBajando(false)
    }
  }

  function escribir(lineas: PerfilLineaWrite[], cabeceraNueva?: Cabecera, alGuardar?: () => void) {
    setError(null)
    setDiff(null)
    setAviso(null)
    const manda = cabeceraNueva ?? actual
    guardar.mutate(
      {
        versionBase: perfil.version_base,
        jornadaDeclarada: manda.jornada || null,
        sedeCabecera: manda.sede || null,
        areaCabecera: manda.area || null,
        jefeEmployeeId: manda.jefe,
        lineas,
      },
      {
        onSuccess: () => {
          setAviso(t('perfilCargo.detalle.guardado'))
          setCabecera(null)
          alGuardar?.()
        },
        onError: (err) => {
          setError(translateApiError(err))
          setDiff(diffDelError(err))
        },
      }
    )
  }

  function adoptar(linea: PerfilLineaRead) {
    adoptarTodas([linea])
  }

  function adoptarTodas(lineas: PerfilLineaRead[]) {
    escribir([
      ...lineasGuardadas(perfil),
      ...lineas.map((linea) => ({
        clave: linea.clave,
        bloque: linea.bloque,
        texto: linea.texto,
        confirmado: CONFIRMADAS_AL_ENTRAR.has(linea.bloque as Bloque),
      })),
    ])
  }

  function quitar(...lineas: PerfilLineaRead[]) {
    const salen = new Set(lineas.map((linea) => linea.clave))
    escribir(lineasGuardadas(perfil).filter((otra) => !salen.has(otra.clave ?? '')))
  }

  // Si cambió el texto, el servidor la vuelve «escrita a mano»; si no, conserva su procedencia (KAI-146).
  function editarLinea(linea: PerfilLineaRead, cambios: PerfilLineaWrite) {
    escribir(
      lineasGuardadas(perfil).map((otra) => (otra.clave === linea.clave ? cambios : otra)),
      undefined,
      () => setApertura(null)
    )
  }

  function confirmarLinea(linea: PerfilLineaRead) {
    escribir(lineasGuardadas(perfil).map((otra) => (otra.clave === linea.clave ? {...otra, confirmado: true} : otra)))
  }

  // El diálogo se cierra al éxito, no al enviar: con un 409 lo tecleado sigue ahí.
  function agregarLineas(lineas: PerfilLineaWrite[]) {
    // Una línea vacía haría que el servidor rechace el borrador entero.
    if (lineas.some((linea) => validateRequired(linea.texto) !== null)) return
    escribir([...lineasGuardadas(perfil), ...lineas], undefined, () => setApertura(null))
  }

  function abrirRiesgo(nueva: AperturaRiesgo) {
    setError(null)
    setDiff(null)
    setRiesgo(nueva)
  }

  function guardarRiesgo(escrito: RiesgoEscrito) {
    const antes = riesgo?.modo === 'editar' ? riesgo.riesgo : null
    escribir(borradorConRiesgo(lineasGuardadas(perfil), antes, escrito), undefined, () => setRiesgo(null))
  }

  function pedirQuitarRiesgo(elegido: Riesgo) {
    setError(null)
    setDiff(null)
    setRiesgoAQuitar(elegido)
  }

  function quitarRiesgo() {
    if (!riesgoAQuitar) return
    escribir(borradorSinRiesgo(lineasGuardadas(perfil), riesgoAQuitar, peligrosGuardados), undefined, () =>
      setRiesgoAQuitar(null)
    )
  }

  function abrirAlcance() {
    setError(null)
    setErrorFirma(null)
    pedirAlcance.mutate(undefined, {
      onSuccess: (data) => {
        setAlcance(data)
        setExigible(data.hoy)
      },
      onError: (err) => setError(translateApiError(err)),
    })
  }

  function firmar(event: FormEvent) {
    event.preventDefault()
    if (!alcance) return
    setErrorFirma(null)
    confirmar.mutate(
      {
        exigibleDesde: exigible,
        motivo: motivo.trim() || null,
        alcanceToken: alcance.alcance_token,
      },
      {
        onSuccess: (data) => {
          setAlcance(null)
          setAviso(
            [
              data.creo_version
                ? t('perfilCargo.confirmado.creado', {version: data.version})
                : t('perfilCargo.confirmado.sinCambios'),
              // Lo retirado se dice: retirar exigencias en silencio es lo que H08 impide.
              data.requisitos_retirados > 0
                ? t('perfilCambio.retirados', {
                    count: data.requisitos_retirados,
                    fecha: formatDateOnly(exigible),
                  })
                : '',
            ]
              .filter(Boolean)
              .join(' ')
          )
          // La confirmación invalida `['perfiles-cargo', businessId]`, prefijo de la clave de versiones.
          void detalle.refetch()
        },
        onError: (err) => {
          // Pedir el alcance es un POST sin efectos: si venció, se recalcula sin cerrar el diálogo.
          if (motivoDelError(err) === null && errorEsAlcanceVencido(err)) {
            setErrorFirma(translateApiError(err))
            pedirAlcance.mutate(undefined, {
              onSuccess: (data) => {
                setAlcance(data)
                setExigible(data.hoy)
              },
              onError: (otro) => setErrorFirma(translateApiError(otro)),
            })
            return
          }
          setErrorFirma(translateApiError(err))
        },
      }
    )
  }

  // Apagado, se ata a `perfil-criterios` con `aria-describedby` en vez de repetir los criterios al lado.
  const accionConfirmar = puedeFirmar && perfil.estado !== 'confirmado' && (
    <Button
      data-testid="perfil-abrir-alcance"
      disabled={pedirAlcance.isPending || !perfil.completo}
      aria-describedby={!perfil.completo ? 'perfil-criterios' : undefined}
      onClick={abrirAlcance}>
      {t('perfilCargo.alcance.confirmar')}
    </Button>
  )

  return (
    <div className="space-y-5" data-testid="perfil-cargo-detalle">
      <Link className="text-sm underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo`}>
        {t('perfilCargo.detalle.volver')}
      </Link>
      <PageHeading title={perfil.cargo} accion={accionConfirmar} />

      <div className="space-y-2" data-testid="perfil-franja">
        {!perfil.completo && (
          <section className="surface rounded-lg p-4" id="perfil-criterios" data-testid="perfil-criterios">
            <p className="flex items-center gap-1.5 text-sm text-foreground">
              {t('perfilCargo.criterios.titulo')}:
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('perfilCargo.criterios.titulo')})}
                testId="perfil-criterios-porque">
                {t('perfilCargo.criterios.nota')}
              </HelpIcon>
            </p>
            <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
              {perfil.criterios_faltantes.map((criterio) => (
                <li key={criterio} data-testid={`criterio-${criterio}`}>
                  {t(`perfilCargo.criterios.${criterio}`)}
                </li>
              ))}
            </ul>
          </section>
        )}
        {perfil.sin_titulares && (
          <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-sin-titulares">
            {t('perfilCargo.sinTitularesDetalle')}
          </p>
        )}
        {!perfil.sin_titulares && perfil.sin_entrevistas && (
          <div
            className="notice-amber max-w-2xl space-y-1 rounded-lg px-3 py-2 text-sm"
            data-testid="perfil-sin-entrevistas">
            <p>{perfil.sin_filas_del_area ? t('perfilCargo.sinEntrevistasNiArea') : t('perfilCargo.sinEntrevistas')}</p>
            <PorQue
              label={t('common.porQue')}
              testId="perfil-sin-entrevistas-porque"
              contenidoTestId="perfil-sin-entrevistas-detalle">
              <p>
                {perfil.sin_filas_del_area
                  ? t('perfilCargo.sinEntrevistasNiAreaPorque')
                  : t('perfilCargo.sinEntrevistasPorque', {area: perfil.areas.join(', ')})}
              </p>
            </PorQue>
          </div>
        )}
        {!perfil.funciones_origen && (
          <div className="max-w-2xl space-y-1" data-testid="perfil-sin-descripcion">
            <p className="text-sm text-muted-foreground">{t('perfilCargo.sinFuncionesEscritas')}</p>
            <PorQue
              label={t('common.porQue')}
              testId="perfil-sin-descripcion-porque"
              contenidoTestId="perfil-sin-descripcion-detalle">
              <p>{t('perfilCargo.sinFuncionesEscritasPorque', {count: perfil.total_personas})}</p>
            </PorQue>
          </div>
        )}
      </div>

      {/* Solo con perfil confirmado: la rejilla no abre sobre un borrador y el DOCX se entrega al Ministerio (E10-H10). */}
      {perfil.estado === 'confirmado' && (
        <div className="flex flex-wrap items-center gap-2" data-testid="perfil-acciones">
          <Button
            size="sm"
            variant="outline"
            disabled={bajando}
            data-testid="perfil-descargar-documento"
            onClick={() => void bajarDocumento()}>
            {t('carpetaVisita.descargarPerfil')}
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link
              to={`/businesses/${businessId}/sgsst/positions/${positionId}/rejilla`}
              data-testid="perfil-ir-a-rejilla">
              {t('cumplimiento.rejilla.titulo', {cargo: perfil.cargo})}
            </Link>
          </Button>
        </div>
      )}
      {errorDocumento && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-documento-error">
          {errorDocumento}
        </p>
      )}

      <PerfilCargoTabs businessId={businessId!} positionId={positionId!} />

      {perfil.estado === 'confirmado' && perfil.confirmado_en && (
        <div className="space-y-1" data-testid="perfil-confirmado">
          <p className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
            <span>
              {t('perfilCargo.confirmado.rotulo', {
                nombre: perfil.confirmado_por_nombre ?? '',
                fecha: formatInstantDate(perfil.confirmado_en),
              })}
              {perfil.exigible_desde && (
                <>
                  {' '}
                  ·{' '}
                  {t('perfilCargo.confirmado.exigible', {
                    fecha: formatDateOnly(perfil.exigible_desde),
                  })}
                </>
              )}
              {perfil.solo_catalogo && <> · {t('perfilCargo.soloCatalogo')}</>}
            </span>
            <HelpIcon label={t('perfilCargo.confirmado.comoCambiarLabel')} testId="perfil-como-cambiar">
              {t('perfilCargo.confirmado.comoCambiar')}
            </HelpIcon>
          </p>
        </div>
      )}
      {/* Fuera del bloque «confirmado»: abrir la versión nueva vuelve borrador el perfil y el aviso se desmontaría (H08). */}
      <AlcanceCambioDialog
        businessId={businessId!}
        positionId={positionId!}
        disabled={godReadOnly}
        confirmado={perfil.estado === 'confirmado'}
      />
      {!!perfil.solicitudes && (
        <p className="text-sm text-muted-foreground" data-testid="perfil-solicitudes-aviso">
          {t('perfilCargo.confirmado.viajoEn', {count: perfil.solicitudes})}{' '}
          <Link className="underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}/versiones`}>
            {t('perfilCargo.tabs.versiones')}
          </Link>
        </p>
      )}
      {aviso && (
        <p className="notice-green max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-detalle-aviso">
          {aviso}
        </p>
      )}
      {error && (
        <div className="max-w-2xl space-y-1">
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-detalle-error">
            {error}
          </p>
          {diff && diff.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-muted-foreground" data-testid="perfil-detalle-diff">
              {diff.map((cambio) => (
                <li key={`${cambio.bloque}-${cambio.cambio}-${cambio.texto}`}>
                  {t(`perfilCargo.diff.${cambio.cambio}`, {
                    bloque: t(`perfilCargo.bloque.${cambio.bloque}`),
                    texto: cambio.texto,
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <section className="surface rounded-lg p-4" data-testid="perfil-cabecera">
        <h2 className="font-heading text-base tracking-tight">{t('perfilCargo.detalle.cabecera')}</h2>
        <form
          className="mt-3 max-w-md space-y-3"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            escribir(lineasGuardadas(perfil), actual)
          }}>
          <div className="space-y-2">
            <Label htmlFor="perfil-jornada">{t('perfilCargo.detalle.jornada')}</Label>
            <Input
              maxLength={tope('ORG')}
              id="perfil-jornada"
              data-testid="perfil-jornada"
              value={actual.jornada}
              disabled={!editable}
              onChange={(event) => setCabecera({...actual, jornada: event.target.value})}
            />
            <PorQue
              label={t('common.deDondeSalio')}
              ariaLabel={`${t('common.deDondeSalio')} — ${t('perfilCargo.detalle.jornada')}`}
              testId="perfil-jornada-origen"
              contenidoTestId="perfil-jornada-origen-contenido">
              <p>{t('perfilCargo.detalle.jornadaOrigen')}</p>
            </PorQue>
          </div>
          <div className="space-y-2">
            <Label htmlFor="perfil-sede">{t('perfilCargo.detalle.sede')}</Label>
            <Input
              maxLength={tope('ORG')}
              id="perfil-sede"
              data-testid="perfil-sede"
              value={actual.sede}
              disabled={!editable}
              onChange={(event) => setCabecera({...actual, sede: event.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="perfil-area">{t('perfilCargo.detalle.area')}</Label>
            <Input
              maxLength={tope('ORG')}
              id="perfil-area"
              data-testid="perfil-area"
              value={actual.area}
              disabled={!editable}
              onChange={(event) => setCabecera({...actual, area: event.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="perfil-jefe">{t('perfilCargo.detalle.jefe')}</Label>
            {editable ? (
              <PersonPicker
                businessId={businessId!}
                testId="perfil-jefe"
                value={{employee_id: actual.jefe ?? undefined}}
                valueLabel={actual.jefe === perfil.cabecera.jefe_employee_id ? perfil.cabecera.jefe_nombre : null}
                onChange={(elegido) => setCabecera({...actual, jefe: elegido.employee_id ?? null})}
              />
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="perfil-jefe">
                {perfil.cabecera.jefe_cargo ?? perfil.cabecera.jefe_nombre ?? t('perfilCargo.detalle.jefeSinDefinir')}
              </p>
            )}
            {editable && actual.jefe === null && (
              <p className="text-xs text-muted-foreground" data-testid="perfil-jefe-vacio">
                {t('perfilCargo.detalle.jefeSinDefinir')}
              </p>
            )}
          </div>
          {editable && (
            <>
              {sucio && (
                <p className="text-xs text-muted-foreground" data-testid="perfil-sin-guardar">
                  {t('perfilCargo.detalle.sinGuardar')}
                </p>
              )}
              <Button type="submit" size="sm" variant="outline" disabled={guardar.isPending}>
                {t('perfilCargo.detalle.guardar')}
              </Button>
            </>
          )}
        </form>
      </section>

      <div className="space-y-4">
        <div className="flex justify-end">
          <TrazabilidadSwitch desplegado={trazabilidad} onChange={setTrazabilidad} testId="perfil-trazabilidad" />
        </div>
        {perfil.bloques.map((bloque) => {
          const acciones = {
            businessId,
            editable,
            pending: guardar.isPending,
            desplegado: trazabilidad,
            onAdoptar: adoptar,
            onAdoptarTodas: adoptarTodas,
            onQuitar: quitar,
            onConfirmarLinea: confirmarLinea,
            onEditar: (linea: PerfilLineaRead) => setApertura({modo: 'editar', linea}),
            onAgregar: setApertura,
          }
          // Las medidas viven DENTRO de la tabla de peligros (KAI-151): no tienen tarjeta.
          if (bloque.bloque === 'medidas_prevencion') return null
          if (bloque.bloque === 'peligros') {
            return (
              <PerfilBloquePeligros
                key={bloque.bloque}
                peligros={bloque}
                medidas={perfil.bloques.find((otro) => otro.bloque === 'medidas_prevencion')}
                businessId={businessId}
                editable={editable}
                pending={guardar.isPending}
                desplegado={trazabilidad}
                onAdoptar={adoptar}
                onAdoptarTodas={adoptarTodas}
                onConfirmarLinea={confirmarLinea}
                onQuitarSueltas={(sueltas) => quitar(...sueltas)}
                onAbrirRiesgo={abrirRiesgo}
                onQuitarRiesgo={pedirQuitarRiesgo}
              />
            )
          }
          if (CON_DESCRIPCION.has(bloque.bloque as Bloque)) {
            return (
              <PerfilBloqueTabla
                key={bloque.bloque}
                bloque={bloque}
                ayuda={
                  <HelpIcon
                    label={t('common.help.iconLabelFor', {campo: t(`perfilCargo.bloque.${bloque.bloque}`)})}
                    testId={`perfil-ayuda-${bloque.bloque}`}>
                    {t(`perfilCargo.ayuda.${bloque.bloque}`)}
                  </HelpIcon>
                }
                {...acciones}
              />
            )
          }
          return (
            <PerfilBloque
              key={bloque.bloque}
              bloque={bloque}
              {...acciones}
              ayuda={
                bloque.bloque === 'funciones' && perfil.funciones_origen ? (
                  <PorQue
                    label={t('common.deDondeSalio')}
                    ariaLabel={`${t('common.deDondeSalio')} — ${t('perfilCargo.bloque.funciones')}`}
                    testId="perfil-funciones-origen"
                    contenidoTestId="perfil-funciones-origen-contenido">
                    <p>
                      {t('perfilCargo.mando', {
                        origen: t(`perfilCargo.origen.${perfil.funciones_origen}`),
                      })}
                    </p>
                  </PorQue>
                ) : undefined
              }
            />
          )
        })}
      </div>

      {!puedeFirmar && capabilities.firmar_bloqueado_por && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-bloqueo-firma">
          {capabilities.firmar_bloqueado_por}
        </p>
      )}

      <PerfilLineaDialog
        apertura={apertura}
        pending={guardar.isPending}
        error={error}
        onCerrar={() => setApertura(null)}
        onAgregar={agregarLineas}
        onGuardar={editarLinea}
      />

      <PerfilRiesgoDialog
        apertura={riesgo}
        peligrosGuardados={peligrosGuardados}
        pending={guardar.isPending}
        error={error}
        onCerrar={() => setRiesgo(null)}
        onGuardar={guardarRiesgo}
      />

      <ConfirmDialog
        open={riesgoAQuitar !== null}
        onOpenChange={(abierto) => !abierto && setRiesgoAQuitar(null)}
        title={t('perfilCargo.riesgo.quitarTitulo')}
        description={t('perfilCargo.riesgo.quitarDetalle', {
          count: riesgoAQuitar ? medidasQueSeVan(riesgoAQuitar, peligrosGuardados).length : 0,
        })}
        confirmLabel={t('perfilCargo.riesgo.quitar')}
        cancelLabel={t('common.cancel')}
        pending={guardar.isPending}
        error={riesgoAQuitar ? error : null}
        onConfirm={quitarRiesgo}
      />

      <PerfilAlcanceDialog
        alcance={alcance}
        exigible={exigible}
        motivo={motivo}
        errorFirma={errorFirma}
        pendiente={confirmar.isPending}
        onExigible={setExigible}
        onMotivo={setMotivo}
        onCerrar={() => setAlcance(null)}
        onFirmar={firmar}
      />
    </div>
  )
}

/** El único rechazo de la confirmación que llega como `detail` plano. */
function errorEsAlcanceVencido(err: unknown): boolean {
  return (err as {response?: {data?: {detail?: unknown}}}).response?.data?.detail === 'the scope preview expired'
}
